// ToolSwitcher — the "LINT" wordmark suffix, optionally an interactive dropdown.
//
// Fetches GET /api/v1/tools to discover which sibling tools (Orbital/Git,
// Orbital/Reg) are enabled + have a URL. When none are configured the component
// renders a plain <span> identical to the old data-tool-switch span in
// Wordmark.tsx, so the nav is visually unchanged. When ≥1 sibling is ready, a
// chevron appears and clicking opens a menu-button dropdown.
//
// Lives outside the home <Link> in TopNav because HTML forbids a <button>
// inside an <a>. T296 lifted this from the data-tool-switch span in Wordmark.
//
// RENDER THIS INSIDE AN `.orb-wordmark` CONTAINER. The `text-[inherit]`,
// `font-[inherit]` and `tracking-[inherit]` classes below only reproduce the
// brand typography if some ancestor actually carries it. When T296 moved this
// out of the Wordmark element, nothing did — so the suffix silently rendered in
// the header's Inter/14px/no-tracking instead of Outfit/15px/0.14em, which also
// changed its line-height and knocked the wordmark off its baseline. TopNav now
// puts `orb-wordmark` on the wrapper around both the Link and this component;
// any new call site has to do the same.
//
// Accessibility: WAI-ARIA menu button pattern — role="menu" + role="menuitem",
// Esc closes + returns focus to trigger, Arrow keys navigate items, Tab cycles
// within the open menu (focus trap).

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import useSWR from 'swr';
import { fetcher, TOOLS_KEY, AvailableTool } from '../lib/api';
import { safeHref } from '../lib/safeHref'

export function ToolSwitcher() {
  const { data: tools = [] } = useSWR<AvailableTool[]>(TOOLS_KEY, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 30_000,
    // On fetch error fall back to empty list → plain wordmark.
    onError: () => undefined,
  });

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const hasTools = tools.length > 0;

  useEffect(() => {
    if (!open) return;

    // Move focus to first menu item when the menu opens.
    const firstItem = containerRef.current?.querySelector<HTMLElement>(
      '[role="menuitem"]',
    );
    firstItem?.focus();

    const getItems = (): HTMLElement[] =>
      Array.from(
        containerRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ??
          [],
      );

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }

      if (e.key === 'Tab') {
        // Focus trap: Tab/Shift+Tab cycles only through menu items.
        const items = getItems();
        if (items.length === 0) return;
        const idx = items.indexOf(document.activeElement as HTMLElement);
        if (e.shiftKey) {
          e.preventDefault();
          items[(idx - 1 + items.length) % items.length].focus();
        } else {
          e.preventDefault();
          items[(idx + 1) % items.length].focus();
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const items = getItems();
        const idx = items.indexOf(document.activeElement as HTMLElement);
        items[(idx + 1) % items.length]?.focus();
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const items = getItems();
        const idx = items.indexOf(document.activeElement as HTMLElement);
        items[(idx - 1 + items.length) % items.length]?.focus();
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open]);

  if (!hasTools) {
    // Plain span — visually identical to the original data-tool-switch span.
    return (
      <span data-tool-switch className="text-accent text-[inherit] font-[inherit] tracking-[inherit]">
        LINT
      </span>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch tool"
        onClick={() => setOpen((o) => !o)}
        className="
          inline-flex items-center gap-px rounded
          text-accent hover:opacity-80 transition-opacity
          focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1
          focus-visible:outline-accent/60
          text-[inherit] font-[inherit] tracking-[inherit]
        "
      >
        <span data-tool-switch>LINT</span>
        <ChevronDown
          className={`h-2.5 w-2.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Orbital tools"
          className="
            absolute left-0 top-full mt-1.5 min-w-[11rem]
            rounded-md border border-border bg-bg-raised shadow-lg
            py-1 z-50
          "
        >
          {/* Current tool — Orbital/Lint — shown as selected, not interactive. */}
          <div
            role="menuitem"
            aria-current="true"
            tabIndex={0}
            className="
              flex items-center gap-2 px-3 py-2
              text-[11px] font-semibold uppercase tracking-[0.08em]
              text-accent bg-accent/10 cursor-default select-none
              focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/60
            "
          >
            <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
            Orbital/Lint
          </div>

          <hr className="my-0.5 border-border" />

          {/* Enabled sibling tools */}
          {tools.map((tool) => (
            <a
              key={tool.key}
              href={safeHref(tool.base_url)}
              role="menuitem"
              tabIndex={0}
              className="
                flex items-center gap-2 px-3 py-2
                text-[11px] font-semibold uppercase tracking-[0.08em]
                text-fg-muted hover:text-fg hover:bg-bg-hover
                transition-colors select-none
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/60
              "
              onClick={() => setOpen(false)}
            >
              <span className="h-3 w-3 shrink-0" aria-hidden="true" />
              {tool.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
